import { Tag, UNTAGGED } from "../tags/tags-slice";

/** The maximum number of tag name suggestions to return by default */
const DEFAULT_SUGGESTIONS_LIMIT = 8;

/**
 * Computes tag name suggestions for the project tree search bar.
 *
 * A tag is suggested when its name starts with the current query (case-insensitive),
 * excluding the special "Untagged" tag and any tag whose name already exactly matches
 * the query
 *
 * @param tags The user project tags to search through
 * @param query The current search text
 * @param limit The maximum number of suggestions to return
 * @returns The matching tag names (original casing), deduplicated and alphabetically sorted
 */
export function getTagNameSuggestions(
  tags: Tag[],
  query: string,
  limit: number = DEFAULT_SUGGESTIONS_LIMIT,
): string[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return [];

  const matches = tags
    .filter((tag) => tag.id !== UNTAGGED.id)
    .map((tag) => tag.name.trim())
    .filter((name) => {
      const normalizedName = name.toLocaleLowerCase();
      // Suggest prefix matches, but not a name that is already fully typed
      return (
        normalizedName !== normalizedQuery &&
        normalizedName.startsWith(normalizedQuery)
      );
    });

  // A Set drops duplicate names before sorting and limiting the results
  return [...new Set(matches)]
    .sort((a, b) => a.localeCompare(b))
    .slice(0, limit);
}
